import type { ResultDebug } from '@empathybroker/tsclient-play/dist/models/search/result'
import type { Schema } from '@empathyco/x-adapter'
import type { Result } from '@empathyco/x-types'
import type { PlayServiceResult } from '../types'
import { decodeExplainGranularityResult } from '@empathybroker/tsclient-play/dist/utils/result-debug-explain-processor.utils'

function composeUrl(value: string | object): string {
  let url: string = value instanceof Object ? Object.values(value)[0] : value
  if (!!url && !url.match(/^https?:\/\//)) {
    url = `https://${url}`
  }
  return url
}

function composeCodeWithName(codes: Array<string> = [], names: Array<string> = []): Array<string> {
  return codes.map((code, index) => `${code} ${names[index]}`)
}

function getResultId(
  result: PlayServiceResult,
  context?: { requestParameters?: Record<string, unknown> },
): string {
  const fieldName = context?.requestParameters?.productsRankingField
  const fieldValue = typeof fieldName === 'string' && fieldName ? result[fieldName] : undefined
  const id = Array.isArray(fieldValue) ? fieldValue[0] : fieldValue

  return id != null && id !== '' ? String(id) : (result.__boostId?.toString() ?? '')
}

export const resultSchema: Schema<any, Result> = {
  modelName: () => 'Result',
  id: getResultId,
  name: '__name',
  url: ({ __url }: PlayServiceResult) => composeUrl(__url),
  imageUrl: ({ __images }: PlayServiceResult) => composeUrl(__images?.[0]),
  debug: (result: PlayServiceResult, context): ResultDebug | undefined => {
    const debugEnabled = context?.requestParameters?.debugEnabled

    if (!debugEnabled || !result.debugProperties) {
      return
    }

    return decodeExplainGranularityResult(result.debugProperties)
  },
  brand: (result: PlayServiceResult) => result?.product?.brandName ?? '',
  cfSize: (result: PlayServiceResult) => result?.product?.customerFacingSize,
  cfDepartments: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.departmentCode, product?.departmentName),
  cfCommodities: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.commodityCode, product?.commodityName),
  cfSubCommodities: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.subCommodityCode, product?.subCommodityName),
  primaryDepartments: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.primaryFamilyCode, product?.primaryFamilyName),
  recapDepartments: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.recapDepartmentFamilyCode, product?.recapDepartmentFamilyName),
  ftDepartments: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.departmentFamilyCode, product?.departmentFamilyName),
  ftCommodities: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.commodityFamilyCode, product?.commodityFamilyName),
  ftSubCommodities: ({ product }: PlayServiceResult) =>
    composeCodeWithName(product?.subCommodityFamilyCode, product?.subCommodityFamilyName),
}
